<!--
  Muestra la etiqueta del catálogo con una clase de color elegida mediante tone. Sin tone
  usa tint-1; off muestra una etiqueta sin relleno para opciones desactivadas. Los tonos
  semánticos indican éxito, aviso o error.

  Con onRemove incluye un botón de retirada; con onclick toda la etiqueta funciona como
  botón. tip usa la capa de ayuda data-tip del panel.
-->
<script lang="ts">
  import type { Snippet } from "svelte";

  import { cx } from "../../lib/cx";
  import Icon from "../Icon.svelte";

  export type Tone =
    | "tint-1"
    | "tint-2"
    | "tint-3"
    | "tint-4"
    | "tint-5"
    | "tint-6"
    | "tint-7"
    | "tint-8"
    | "tint-9"
    | "tint-10"
    | "tag-success"
    | "tag-warning"
    | "tag-error"
    | "off";

  let {
    tone = "tint-1",
    class: className,
    tip,
    tipSide,
    onclick,
    pressed,
    ariaLabel,
    onRemove,
    removeLabel,
    children,
  }: {
    /** El color, por lo que significa. `off` es la pastilla apagada. */
    tone?: Tone;
    class?: string;
    /** El globo de ayuda. Lo dibuja `TooltipLayer`, no la etiqueta. */
    tip?: string;
    /** De qué lado sale el globo. Por defecto, arriba. */
    tipSide?: "top" | "bottom" | "left" | "right";
    /** Si viene, la etiqueta entera es un botón. */
    onclick?: () => void;
    /** Para la etiqueta-botón que se queda encendida, como un filtro puesto. */
    pressed?: boolean;
    /** Lo que dice la etiqueta-botón en voz alta, si su texto solo no basta. */
    ariaLabel?: string;
    /** Si viene, dentro sale la cruz de quitar. */
    onRemove?: () => void;
    /** Que se quita, para quien no ve la cruz. Por defecto, "Quitar". */
    removeLabel?: string;
    children: Snippet;
  } = $props();

  const classes = $derived(cx(className, "tag", tone !== "off" && tone));
</script>

{#snippet body()}
  {@render children()}
  {#if onRemove}
    <!--
      El botón no hereda el toque de la etiqueta: quitar y abrir son dos
      cosas distintas, y una etiqueta-botón con cruz las tendría pegadas.
    -->
    <button
      type="button"
      class="tag-remove"
      aria-label={removeLabel ?? "Quitar"}
      data-tip="Quitar"
      onclick={(e) => {
        e.stopPropagation();
        onRemove?.();
      }}
    >
      <Icon name="cancel-01" size={12} />
    </button>
  {/if}
{/snippet}

{#if onclick}
  <button
    type="button"
    {onclick}
    class={classes}
    aria-label={ariaLabel}
    aria-pressed={pressed}
    data-tip={tip}
    data-tip-side={tip ? tipSide : undefined}
  >
    {@render body()}
  </button>
{:else}
  <span class={classes} data-tip={tip} data-tip-side={tip ? tipSide : undefined}>
    {@render body()}
  </span>
{/if}
